/**
 * Socket.io connection hook
 * 
 * Fixes applied:
 * - wireAudioListeners now reads selfUserId from store at event time (not at connect time)
 *   so users no longer hear their own voice back when socket connects before user loads
 * - On socket reconnect (connect event after disconnect), re-emit channel:join
 *   so the user rejoins their channel automatically after reconnect
 * - socketStatus updates (connected/disconnected/reconnecting) for the Header indicator
 * - AudioContext is resumed on ptt:audioChunk to comply with browser autoplay policy
 * - ptt:transcription event wired to add transcription messages to channel log
 * - ptt:stopped delayed 250ms to absorb network reordering (last chunk may arrive after stop event)
 */
import { useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import axios from 'axios';
import useStore from '../store/useStore';

let socketInstance = null;

export function getSocket() {
  return socketInstance;
}

export { playPTTBeep };

// ── Audio Engine ─────────────────────────────────────────────────────────
// Primary: live MSE playback while the transmitter is sending (see liveStreams).
// Fallback: chunks accumulate and playback fires on ptt:stopped (iOS / MSE failure).
const pendingChunks = {};       // senderId -> Uint8Array[] (iOS fallback)
const iOSFallbackPending = {};  // senderId -> true when blob failed, waiting for mp3 URL
const pendingMp3Urls    = {};  // senderId -> mp3Url (cached from ptt:audioReady, may arrive before blob fails)



// Detect if this browser can decode webm audio (iOS Safari cannot)
const canPlayWebM = (() => {
  try {
    const a = document.createElement('audio');
    return a.canPlayType('audio/webm; codecs=opus') !== '' ||
           a.canPlayType('audio/webm') !== '';
  } catch { return false; }
})();

// Visible on-screen debug log (temporary — shows on all devices)

// ── iOS Audio Strategy ───────────────────────────────────────────────────
// Primary: new Audio(url).play() — works via iOS sticky activation (user has
// interacted with page at least once). Much simpler than Web Audio API.
// Fallback: Web Audio API decodeAudioData — when HTML Audio fails.
// Keep-alive: silent pulse every 20s to prevent AudioContext suspension.
let audioCtx = null;
let iosAudioUnlocked = false;
const iosAudioQueue = []; // mp3 URLs queued if nothing else works
let keepAliveInterval = null;

function getAudioCtx() {
  if (!audioCtx) {
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    } catch(e) { return null; }
  }
  return audioCtx;
}

// Keep AudioContext alive — play 1 sample of silence every 20s
// Prevents iOS from auto-suspending the context between transmissions
function startAudioKeepAlive() {
  if (keepAliveInterval) return;
  keepAliveInterval = setInterval(() => {
    const ctx = audioCtx;
    if (!ctx) return;
    if (ctx.state === 'suspended') { ctx.resume().catch(() => {}); return; }
    try {
      const buf = ctx.createBuffer(1, 1, 8000);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      src.start(0);
    } catch(_) {}
  }, 20000);
}

// ── PTT Radio Beeps — rango humano 400-900 Hz, amigable con bocinas/perros ──
function playPTTBeep(type) {
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume();
    const now = ctx.currentTime;

    // Soft compressor — evita clipping sin distorsión
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -18;
    comp.knee.value = 6;
    comp.ratio.value = 3;
    comp.attack.value = 0.003;
    comp.release.value = 0.1;
    comp.connect(ctx.destination);

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.45, now); // volumen moderado, no agresivo
    master.connect(comp);

    if (type === 'start') {
      // Dos blips ascendentes suaves: 420→680 Hz + 520→820 Hz
      // Blip 1
      const o1 = ctx.createOscillator(), g1 = ctx.createGain();
      o1.type = 'sine';
      o1.frequency.setValueAtTime(420, now);
      o1.frequency.linearRampToValueAtTime(680, now + 0.07);
      g1.gain.setValueAtTime(0, now);
      g1.gain.linearRampToValueAtTime(0.8, now + 0.008);
      g1.gain.linearRampToValueAtTime(0, now + 0.07);
      o1.connect(g1); g1.connect(master);
      o1.start(now); o1.stop(now + 0.08);

      // Blip 2 (90ms después, un poco más alto)
      const o2 = ctx.createOscillator(), g2 = ctx.createGain();
      o2.type = 'sine';
      o2.frequency.setValueAtTime(520, now + 0.09);
      o2.frequency.linearRampToValueAtTime(820, now + 0.16);
      g2.gain.setValueAtTime(0, now + 0.09);
      g2.gain.linearRampToValueAtTime(0.9, now + 0.095);
      g2.gain.linearRampToValueAtTime(0, now + 0.16);
      o2.connect(g2); g2.connect(master);
      o2.start(now + 0.09); o2.stop(now + 0.17);

    } else {
      // Roger beep clásico: 860→440 Hz, limpio y corto
      const o1 = ctx.createOscillator(), g1 = ctx.createGain();
      o1.type = 'sine';
      o1.frequency.setValueAtTime(860, now);
      o1.frequency.linearRampToValueAtTime(440, now + 0.15);
      g1.gain.setValueAtTime(0.9, now);
      g1.gain.setValueAtTime(0.9, now + 0.1);
      g1.gain.linearRampToValueAtTime(0, now + 0.15);
      o1.connect(g1); g1.connect(master);
      o1.start(now); o1.stop(now + 0.16);

      // Echo suave (200ms después, 30% volumen)
      const o2 = ctx.createOscillator(), g2 = ctx.createGain();
      o2.type = 'sine';
      o2.frequency.setValueAtTime(860, now + 0.2);
      o2.frequency.linearRampToValueAtTime(440, now + 0.32);
      g2.gain.setValueAtTime(0.28, now + 0.2);
      g2.gain.linearRampToValueAtTime(0, now + 0.32);
      o2.connect(g2); g2.connect(master);
      o2.start(now + 0.2); o2.stop(now + 0.33);
    }
  } catch(_) {}
}

function unlockIOSAudio() {
  const ctx = getAudioCtx();
  if (!ctx) return;
  if (ctx.state === 'running') {
    iosAudioUnlocked = true;
    return;
  }
  ctx.resume().then(() => {
    // Play 1 sample of silence to fully unlock
    try {
      const buf = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      src.start(0);
    } catch(_) {}
    iosAudioUnlocked = true;
    startAudioKeepAlive(); // prevent iOS from suspending AudioContext
    console.log('[Audio] AudioContext unlocked, state:', ctx.state);
    // Drain queued URLs
    while (iosAudioQueue.length > 0) {
      playIOSUrl(iosAudioQueue.shift());
    }
  }).catch(e => console.warn('[Audio] ctx.resume failed:', e));
}

function playIOSUrl(url) {
  const ctx = getAudioCtx();

  const viaWebAudio = (context) => fetch(url)
    .then(r => r.arrayBuffer())
    .then(buf => context.decodeAudioData(buf))
    .then(decoded => {
      const src = context.createBufferSource();
      src.buffer = decoded;
      src.connect(context.destination);
      src.start(0);
    });

  const viaHTMLAudio = () => {
    const audio = new Audio(url);
    audio.setAttribute('playsinline', '');
    audio.setAttribute('webkit-playsinline', '');
    return audio.play();
  };

  if (ctx && ctx.state === 'running') {
    // Best path: AudioContext unlocked by user gesture — decodeAudioData works reliably
    viaWebAudio(ctx).catch(e => {
      viaHTMLAudio().catch(e2 => { iosAudioQueue.push(url); });
    });
  } else if (ctx) {
    // Try resume (works if user interacted recently via sticky activation)
    ctx.resume().then(() => {
      return viaWebAudio(ctx);
    }).catch(e => {
      return viaHTMLAudio();
    }).catch(e2 => {
      iosAudioQueue.push(url);
    });
  } else {
    viaHTMLAudio().catch(e => { iosAudioQueue.push(url); });
  }
}
const senderMimeTypes = {};     // senderId -> mimeType string
const playTimers = {};          // senderId -> failsafe setTimeout handle

// ── Live streaming playback (MSE) ────────────────────────────────────────
// Receivers play chunks AS THEY ARRIVE via MediaSource instead of waiting
// for ptt:stopped (real radio feel, ~300ms behind the speaker).
// pendingChunks keeps accumulating in parallel, so any mid-stream failure
// falls back to the pre-existing accumulate-and-play path (and iOS Safari,
// which has no MSE-webm support, never enters this path at all).
const liveStreams = {}; // senderId -> { sb, audio, queue, ended, failed, ms }

function mseSupportedFor(mime) {
  try {
    return typeof window !== 'undefined' && 'MediaSource' in window &&
      mime.startsWith('audio/webm') && MediaSource.isTypeSupported(mime);
  } catch { return false; }
}

function pumpLive(state) {
  if (state.failed || !state.sb || state.sb.updating) return;
  if (state.queue.length > 0) {
    const next = state.queue.shift();
    try { state.sb.appendBuffer(next); }
    catch (e) { failLive(state, 'appendBuffer: ' + e.message); }
  } else if (state.ended && state.ms.readyState === 'open') {
    try { state.ms.endOfStream(); } catch (_) {}
  }
}

function failLive(state, why) {
  if (state.failed) return;
  state.failed = true;
  console.warn('[LiveAudio] falling back to buffered playback:', why);
  try { state.audio.pause(); } catch (_) {}
  try { URL.revokeObjectURL(state.audio.src); } catch (_) {}
  // Entry stays in liveStreams marked failed; ptt:stopped sees failed=true
  // and routes to playAccumulated (chunks kept accumulating in parallel).
}

function startLiveStream(senderId, mime) {
  const ms = new MediaSource();
  const audio = new Audio();
  audio.preload = 'auto';
  const state = { ms, sb: null, audio, queue: [], ended: false, failed: false };
  liveStreams[senderId] = state;

  audio.onerror = () => failLive(state, 'audio element error');
  audio.onended = () => { try { URL.revokeObjectURL(audio.src); } catch (_) {} };
  ms.addEventListener('sourceopen', () => {
    if (state.failed) return;
    try {
      state.sb = ms.addSourceBuffer(mime);
    } catch (e) { return failLive(state, 'addSourceBuffer: ' + e.message); }
    state.sb.mode = 'sequence';
    state.sb.addEventListener('updateend', () => pumpLive(state));
    state.sb.addEventListener('error', () => failLive(state, 'sourceBuffer error'));
    pumpLive(state);
  });
  audio.src = URL.createObjectURL(ms);
  const p = audio.play();
  if (p !== undefined) p.catch(err => failLive(state, 'play() blocked: ' + err.name));
  return state;
}

// Finish a live stream: flush queue, end MSE, free the map slot shortly after
// (late chunks within that window are still appended; after that they're ignored
// because a new transmission always begins with ptt:transmitting, which clears the slot).
function endLiveStream(senderId) {
  const state = liveStreams[senderId];
  if (!state) return;
  state.ended = true;
  pumpLive(state);
  setTimeout(() => { if (liveStreams[senderId] === state) delete liveStreams[senderId]; }, 5000);
}

function destroyLiveStream(senderId) {
  const state = liveStreams[senderId];
  if (!state) return;
  failLive(state, 'destroyed');
  delete liveStreams[senderId];
}

// We keep a single unlocked <audio> element that acts as an "unlock token"
// on iOS/Android. Once play() succeeds on a user gesture, subsequent
// programmatic plays on new Audio() elements are allowed.
let audioUnlocked = false;

function ensureAudioUnlocked() {
  // Register unlock on ANY user interaction — covers both HTML Audio and Web Audio API
  const tryUnlock = () => {
    // Unlock Web Audio API (needed for iOS Safari programmatic playback)
    unlockIOSAudio();
    // Also unlock HTML Audio (for desktop non-webm blob playback)
    if (!audioUnlocked) {
      audioUnlocked = true; // mark optimistically; actual play uses webm blob which works on desktop
    }
  };
  document.addEventListener('click', tryUnlock, { once: true, capture: true });
  document.addEventListener('touchstart', tryUnlock, { once: true, capture: true });
  document.addEventListener('keydown', tryUnlock, { once: true, capture: true });
}

// Resume AudioContext on first user interaction (belt + suspenders)
function resumeAudioCtxOnInteraction() {
  ensureAudioUnlocked();
}

async function playAccumulated(senderId) {
  // Cancel failsafe timer
  if (playTimers[senderId]) { clearTimeout(playTimers[senderId]); delete playTimers[senderId]; }

  const chunks = pendingChunks[senderId];
  const mimeType = senderMimeTypes[senderId] || 'audio/webm;codecs=opus';
  if (!chunks || chunks.length === 0) return;

  // Consume immediately to avoid duplicate plays
  delete pendingChunks[senderId];
  delete senderMimeTypes[senderId];

  try {
    // Build blob from accumulated Uint8Array chunks
    const blob = new Blob(chunks.map(u => u instanceof Uint8Array ? u : new Uint8Array(u)), { type: mimeType });
    if (blob.size === 0) { console.warn('[Audio] Empty blob — skipping'); return; }

    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    audio.preload = 'auto';

    const cleanup = () => {
      URL.revokeObjectURL(url);
      audio.onended = null;
      audio.onerror = null;
    };
    audio.onended = cleanup;
    audio.onerror = (e) => {
      console.warn('[Audio] Element error (will try mp3 fallback if available):', e?.message || e);
      cleanup();
      // Mark this sender as needing URL fallback (for iOS / webm-unsupported browsers)
      iOSFallbackPending[senderId] = true;
    };

    // iOS Safari: can't play webm blobs — ptt:audioReady will handle playback via mp3
    if (!canPlayWebM) {
      cleanup();
      return; // ptt:audioReady listener will play the mp3
    }
    console.log('[Audio] Playing', chunks.length, 'chunks,', blob.size, 'bytes, fmt:', mimeType, 'from:', senderId);
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      await playPromise.then(() => {
      }).catch(err => {
        // NotAllowedError = autoplay blocked
        console.warn('[Audio] play() blocked:', err.name, err.message);
        cleanup();
        const cachedUrl = pendingMp3Urls[senderId];
        if (cachedUrl) {
          // mp3 URL already arrived — play it now
          delete pendingMp3Urls[senderId];
          playIOSUrl(cachedUrl);
        } else {
          // mp3 URL not yet arrived — mark pending and wait
          iOSFallbackPending[senderId] = true;
        }
      });
    }
  } catch (err) {
    console.warn('[Audio] Playback error:', err.message);
  }
}

function wireAudioListeners(socket) {
  // FIX: Read selfUserId from store at event time, not at connect time.
  // Previously, selfUserId was captured when socket connected — if user wasn't
  // loaded yet, it was undefined and the user would hear their own voice.
  // Track sender's audio format from ptt:transmitting
  socket.on('ptt:transmitting', ({ userId: senderId, mimeType }) => {
    if (senderId && mimeType) senderMimeTypes[senderId] = mimeType;
    // A new transmission is starting — clear any leftover live stream slot
    if (senderId) destroyLiveStream(senderId);
    const selfId = useStore.getState().user?.id;
    if (senderId && senderId !== selfId) playPTTBeep('start');
  });

  socket.on('ptt:audioChunk', ({ userId: senderId, chunk, channelId }) => {
    const selfUserId = useStore.getState().user?.id; // read at event time
    if (senderId === selfUserId) return;
    // Skip if channel is muted
    if (channelId && useStore.getState().mutedChannels.has(channelId)) return;
    const u8 = chunk instanceof Uint8Array ? chunk : new Uint8Array(chunk);
    // Always accumulate — fallback path if live playback fails mid-stream
    if (!pendingChunks[senderId]) pendingChunks[senderId] = [];
    pendingChunks[senderId].push(u8);
    // Live playback via MSE (first chunk starts the stream)
    const mime = senderMimeTypes[senderId] || 'audio/webm;codecs=opus';
    let live = liveStreams[senderId];
    if (live === undefined && canPlayWebM && mseSupportedFor(mime)) {
      live = startLiveStream(senderId, mime);
    }
    if (live && !live.failed && !live.ended) {
      live.queue.push(u8);
      pumpLive(live);
    }
    if (playTimers[senderId]) clearTimeout(playTimers[senderId]);
    playTimers[senderId] = setTimeout(() => {
      // Failsafe: no ptt:stopped arrived. If live playback already delivered
      // the audio, just close the stream; otherwise play the buffer.
      if (liveStreams[senderId] && !liveStreams[senderId].failed) {
        delete pendingChunks[senderId];
        endLiveStream(senderId);
      } else {
        playAccumulated(senderId);
      }
    }, 65000);
  });

  socket.on('ptt:stopped', ({ userId: senderId }) => {
    const selfUserId = useStore.getState().user?.id;
    if (senderId === selfUserId) return;
    playPTTBeep('end');
    const live = liveStreams[senderId];
    if (live && !live.failed) {
      // Live playback already delivered the audio — don't replay the buffer
      if (playTimers[senderId]) { clearTimeout(playTimers[senderId]); delete playTimers[senderId]; }
      delete pendingChunks[senderId];
      delete senderMimeTypes[senderId];
      setTimeout(() => endLiveStream(senderId), 250); // absorb late chunks
    } else {
      setTimeout(() => playAccumulated(senderId), 250);
    }
  });

  // ptt:audioReady — mp3 ready on server; play if: (a) can't play webm, or (b) blob was blocked
  socket.on('ptt:audioReady', ({ transmissionId, channelId, mp3Url, userId: senderId }) => {
    const selfUserId = useStore.getState().user?.id;
    if (senderId && senderId === selfUserId) return; // don't play own voice back
    const blobAlreadyFailed = !!iOSFallbackPending[senderId];
    if (!canPlayWebM || blobAlreadyFailed) {
      // Either: can never play webm, OR: blob already failed — play mp3 now
      delete iOSFallbackPending[senderId];
      delete pendingMp3Urls[senderId];
      playIOSUrl(mp3Url);
    } else {
      // canPlayWebM=true and blob hasn't failed yet — cache URL in case blob fails
      pendingMp3Urls[senderId] = mp3Url;
    }
  });

  socket.on('channel:userLeft', ({ userId: senderId }) => {
    destroyLiveStream(senderId);
    delete pendingChunks[senderId];
    delete pendingMp3Urls[senderId];
    delete iOSFallbackPending[senderId];
  });
}

// ── Geolocation tracking ─────────────────────────────────────────────────
let geoWatchId = null;

function startLocationTracking(socket) {
  if (!navigator.geolocation) return;
  if (geoWatchId !== null) return; // already tracking

  const sendLocation = (pos) => {
    if (!socket?.connected) return;
    socket.emit('user:location', {
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
      heading: pos.coords.heading,
      speed: pos.coords.speed,
    });
  };

  // Send immediately on connect
  navigator.geolocation.getCurrentPosition(sendLocation, () => {}, { timeout: 8000 });

  // Then watch for changes (updates every ~10s or on movement)
  geoWatchId = navigator.geolocation.watchPosition(sendLocation, () => {}, {
    enableHighAccuracy: true,
    maximumAge: 5000,
    timeout: 15000,
  });
}

function stopLocationTracking() {
  if (geoWatchId !== null) {
    navigator.geolocation.clearWatch(geoWatchId);
    geoWatchId = null;
  }
}

// -----------------------------------------------------------------------

export default function useSocket() {
  const socketRef = useRef(null);
  const token = useStore(s => s.token);
  const isFirstConnect = useRef(true);

  const setChannelUsers = useStore(s => s.setChannelUsers);
  const addChannelUser = useStore(s => s.addChannelUser);
  const removeChannelUser = useStore(s => s.removeChannelUser);
  const updateChannelCount = useStore(s => s.updateChannelCount);
  const setTransmitting = useStore(s => s.setTransmitting);
  const setUserOnline = useStore(s => s.setUserOnline);
  const setUserOffline = useStore(s => s.setUserOffline);
  const addMessage = useStore(s => s.addMessage);
  const setChannelJoinError = useStore(s => s.setChannelJoinError);
  const setSocketStatus = useStore(s => s.setSocketStatus);
  const addToast = useStore(s => s.addToast);

  const connect = useCallback((userId) => {
    if (socketInstance?.connected) return socketInstance;

    const socket = io('/', {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5000,
    });

    isFirstConnect.current = true;

    socket.on('connect', () => {
      resumeAudioCtxOnInteraction(); // ensure audio unlocked on next interaction
      // Drain any queued iOS URLs that arrived before audio was unlocked
      setTimeout(() => {
        while (iosAudioQueue.length > 0) playIOSUrl(iosAudioQueue.shift());
      }, 500);
      console.log('[Socket] Connected:', socket.id);
      setSocketStatus('connected');

      if (!isFirstConnect.current) {
        // Reconnect: re-join channel if user was in one
        const currentChannel = useStore.getState().currentChannel;
        if (currentChannel) {
          console.log('[Socket] Reconnect: rejoining channel', currentChannel.id, currentChannel.name);
          socket.emit('channel:join', { channelId: currentChannel.id });
        }
      }
      isFirstConnect.current = false;
    });

    socket.on('disconnect', (reason) => {
      console.log('[Socket] Disconnected:', reason);
      stopLocationTracking();
      setTransmitting(false, null);
      // If reconnection is in progress, show reconnecting; else show disconnected
      if (socket.io.reconnection() && reason !== 'io client disconnect') {
        setSocketStatus('reconnecting');
      } else {
        setSocketStatus('disconnected');
      }
    });

    socket.io.on('reconnect_attempt', (attempt) => {
      console.log('[Socket] Reconnect attempt', attempt);
      setSocketStatus('reconnecting');
    });

    socket.io.on('reconnect_failed', () => {
      console.error('[Socket] Reconnect failed');
      setSocketStatus('disconnected');
    });

    socket.on('connect_error', (err) => {
      console.error('[Socket] Error:', err.message);
      setSocketStatus('disconnected');
    });

    // Channel events
    socket.on('channel:users', ({ channelId, users }) => {
      setChannelUsers(channelId, users);
      updateChannelCount(channelId, users.length);
    });

    // ── Auto-joined channels (backend assigns on connect) ────────
    socket.on('channel:autoJoined', ({ channelIds, channels: eventChannels }) => {
      const { user, setAssignedChannels, setActiveChannel } = useStore.getState();
      // Prefer channels from event payload (avoids race condition on page reload
      // where user may not be hydrated yet). Fall back to user.assignedChannels.
      const channels = (eventChannels && eventChannels.length > 0)
        ? eventChannels
        : (user?.assignedChannels || []);
      if (channels.length > 0) {
        setAssignedChannels(channels);
        // Set primary channel as active
        const primary = channels.find(c => c.isPrimary) || channels[0];
        if (primary) setActiveChannel(primary);
      }
    });

    socket.on('channel:joined', async (data) => {
      if (setChannelJoinError) setChannelJoinError(null);
      // Load recent text/image messages for this channel — single batch update
      const channelId = data?.channelId || useStore.getState().currentChannel?.id;
      if (channelId) {
        try {
          const res = await axios.get(`/api/messages/${channelId}?limit=50`);
          if (res.data?.length > 0) {
            const { setMessages, messages: existingMsgs } = useStore.getState();
            const existing = existingMsgs[channelId] || [];
            const existingIds = new Set(existing.map(m => m.id));
            // Merge: historical first, then real-time events (deduplicated)
            const historical = res.data.filter(m => !existingIds.has(m.id));
            setMessages(channelId, [...historical, ...existing]);
          }
        } catch {
          // non-critical
        }
      }
    });

    socket.on('channel:joinError', ({ channelId, error }) => {
      if (setChannelJoinError) setChannelJoinError({ channelId, error });
    });

    socket.on('channel:userJoined', ({ userId, username, avatarColor, channelId }) => {
      addChannelUser(channelId, { userId, username, avatarColor });
    });

    socket.on('channel:userLeft', ({ userId, channelId }) => {
      removeChannelUser(channelId, userId);
      const { removeTransmittingUser } = useStore.getState();
      if (channelId && userId) removeTransmittingUser(channelId, userId);
    });

    socket.on('channel:usersCount', ({ channelId, count }) => {
      updateChannelCount(channelId, count);
    });

    // ── Chat messages ─────────────────────────────────────────────────────
    socket.on('channel:message', (msg) => {
      const { addMessage, messages } = useStore.getState();
      // Deduplicate: skip if message with same id already in store
      const existing = messages[msg.channelId] || [];
      if (existing.some(m => m.id === msg.id)) return;
      addMessage(msg.channelId, msg);
    });

    // Edit / delete (broadcast from REST endpoints)
    socket.on('message:updated', ({ id, channelId, content, editedAt, mentions }) => {
      const { updateMessage } = useStore.getState();
      updateMessage(channelId, id, { content, editedAt, mentions });
    });

    socket.on('message:deleted', ({ id, channelId, deletedAt, deletedBy }) => {
      const { updateMessage } = useStore.getState();
      updateMessage(channelId, id, { deletedAt, deletedBy, content: null });
    });

    // PTT state events
    socket.on('ptt:transmitting', ({ userId, username, avatarColor, channelId }) => {
      setTransmitting(true, { userId, username, avatarColor });
      const { addTransmittingUser } = useStore.getState();
      if (channelId && userId) addTransmittingUser(channelId, userId);
      addMessage(channelId, {
        id: Date.now() + Math.random(),
        type: 'ptt_start',
        userId, username, avatarColor,
        timestamp: new Date().toISOString(),
      });
    });

    // tempPttIds: userId -> tempId for correlating transmissionId with message
    const tempPttIds = {};
    socket.on('ptt:stopped', ({ userId, username, channelId, duration }) => {
      setTransmitting(false, null);
      const { removeTransmittingUser } = useStore.getState();
      if (channelId && userId) removeTransmittingUser(channelId, userId);
      if (duration && duration > 0) {
        const tempId = 'ptt_' + userId + '_' + Date.now();
        tempPttIds[userId] = { tempId, channelId };
        addMessage(channelId, {
          id: tempId,
          type: 'ptt_end',
          userId, username, duration,
          timestamp: new Date().toISOString(),
        });
      }
    });
    // Backend sends transmissionId after saving — remap message id
    socket.on('ptt:transmissionId', ({ userId: senderId, transmissionId, channelId }) => {
      const entry = tempPttIds[senderId];
      if (!entry) return;
      const { updateMessage } = useStore.getState();
      // Remap: change the temp id to the real transmissionId
      updateMessage(entry.channelId, entry.tempId, { id: transmissionId });
      tempPttIds[senderId] = { tempId: transmissionId, channelId };
    });

    // Transcription event — update existing PTT message bubble inline
    socket.on('ptt:transcription', ({ userId, username, channelId, transmissionId, text, language }) => {
      if (!text?.trim()) return;
      const { updateMessage } = useStore.getState();
      // Try to update the PTT message that has this transmissionId
      updateMessage(channelId, transmissionId, { transcription: text, language });
    });

    // ── SOS broadcasts ──────────────────────────────────────────────────
    socket.on('sos_alert', ({ userId, username, avatarColor, channelId, lat, lng, accuracy, message, timestamp }) => {
      const { addMessage, addToast, user } = useStore.getState();
      const ts = timestamp || new Date().toISOString();
      const coordTxt = (typeof lat === 'number' && typeof lng === 'number')
        ? `${lat.toFixed(4)}, ${lng.toFixed(4)}` : 'sin GPS';
      const hh = new Date(ts).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
      // Push into the emergency channel log if channelId provided
      if (channelId) {
        addMessage(channelId, {
          id: 'sos_' + (timestamp || Date.now()) + '_' + userId + Math.random(),
          type: 'sos',
          userId, username, avatarColor,
          content: message || `🚨 SOS — ${username} · ${coordTxt} · ${hh}`,
          coords: (typeof lat === 'number' && typeof lng === 'number') ? { lat, lng, accuracy } : null,
          timestamp: ts,
        });
      }
      // Toast for everyone receiving (skip if it's our own outgoing — already toasted)
      if (userId && userId !== user?.id) {
        addToast(`🚨 SOS de ${username}`, 'error');
      }
    });

    // User presence
    socket.on('user:online', ({ userId, username, avatarColor }) => {
      setUserOnline(userId, { username, avatarColor });
    });
    socket.on('user:offline', ({ userId }) => {
      setUserOffline(userId);
    });

    // Wire audio listeners (reads userId from store at event time — see wireAudioListeners)
    wireAudioListeners(socket);

    // Start geolocation tracking
    startLocationTracking(socket);

    socketInstance = socket;
    socketRef.current = socket;
    return socket;
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  // Note: connect/disconnect are excluded from deps intentionally — they reference stable
  // store actions and adding them would cause the effect to re-run on every render.

  const disconnect = useCallback(() => {
    if (socketInstance) {
      socketInstance.disconnect();
      socketInstance = null;
    }
    socketRef.current = null;
  }, []);

  const joinChannel = useCallback((channelId, password) => {
    if (socketInstance?.connected) {
      socketInstance.emit('channel:join', { channelId, password });
    }
  }, []);

  const leaveChannel = useCallback(() => {
    if (socketInstance?.connected) {
      socketInstance.emit('channel:leave');
    }
  }, []);

  return { connect, disconnect, joinChannel, leaveChannel };
}
